/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationNode,
  GamoraFoundationNodeArgs,
  GamoraFoundationNodeChildMap,
  GamoraFoundationNodeRoot,
} from '../node/node.js';
import { assert } from '../../utils/assert.js';
import { debugCreate } from '../../utils/debug.js';

const debug = debugCreate({ namespace: 'gamora:foundation:glue' });

/**
 * Listener function
 * @internal
 */
export type GamoraFoundationGlueListenerType = (...args: any[]) => any;

/**
 * Default namespace
 * @public
 */
export const GamoraFoundationGlueDefaultNamespace = 'default';

/**
 * GamoraFoundationGlue
 *
 * Glue class
 * @internal
 */
export class GamoraFoundationGlue<
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap>,
  TRoot extends GamoraFoundationNodeRoot = any,
  TSuper extends GamoraFoundationNodeRoot = any,
> extends GamoraFoundationNode<TChildMap, TRoot, TSuper> {
  protected _$eventListenersMap: Map<
    string,
    Map<string, GamoraFoundationGlueListenerType[]>
  >;
  protected _$invokerListenersMap: Map<
    string,
    Map<string, GamoraFoundationGlueListenerType>
  >;
  protected _$yielderListenersMap: Map<
    string,
    Map<string, GamoraFoundationGlueListenerType>
  >;
  constructor(args: GamoraFoundationNodeArgs<TChildMap>) {
    super(args);
    this._$eventListenersMap = new Map();
    this._$invokerListenersMap = new Map();
    this._$yielderListenersMap = new Map();
  }

  /**
   * Registers an invoker listener
   * @param namespace - The namespace
   * @param name - The invoker name
   * @param listener - The listener function
   */
  $registerInvoker(
    namespace: string,
    name: string,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    assert(typeof listener === 'function', 'Invalid listener argument.');
    if (!this._$invokerListenersMap.has(namespace)) {
      this._$invokerListenersMap.set(namespace, new Map());
    }
    const namespacedListenerMap = this._$invokerListenersMap.get(
      namespace,
    ) as Map<string, GamoraFoundationGlueListenerType>;
    namespacedListenerMap.set(name, listener);
    return this;
  }

  /**
   * Unregisters an invoker listener
   * @param namespace - The namespace
   * @param name - The invoker name
   * @param listener - The listener function
   */
  $unregisterInvoker(namespace: string, name: string): this {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    if (this._$invokerListenersMap.has(namespace)) {
      const namespacedListenerMap = this._$invokerListenersMap.get(
        namespace,
      ) as Map<string, GamoraFoundationGlueListenerType>;
      namespacedListenerMap.delete(name);
      if (namespacedListenerMap.size === 0) {
        this._$invokerListenersMap.delete(namespace);
      }
    }
    return this;
  }

  /**
   * Checks if an invocation exists
   * @param namespace - The namespace
   * @param name - The name
   */
  $hasInvocation(namespace: string, name: string): boolean {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    const namespacedListenerMap = this._$invokerListenersMap.get(namespace);
    if (!namespacedListenerMap) {
      return false;
    }
    const listener = namespacedListenerMap.get(name);
    if (!listener) {
      return false;
    }
    return true;
  }

  /**
   * Executes the invoker
   * @param name - The event name
   * @param listener - The listener function
   */
  $executeInvocation<T>(namespace: string, name: string, ...args: any[]): T {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    const namespacedListenerMap = this._$invokerListenersMap.get(namespace);
    assert(
      namespacedListenerMap !== undefined,
      'Invocation failed: ' +
        namespace +
        '/' +
        name +
        ' for ' +
        this.constructor.name +
        ' is not registered.',
    );
    const listener = namespacedListenerMap.get(name);
    assert(
      listener !== undefined,
      'Invocation failed: ' +
        namespace +
        '/' +
        name +
        ' for ' +
        this.constructor.name +
        ' is not registered.',
    );
    debug.log(
      '($executeInvocation): Running invocation ' +
        namespace +
        '/' +
        name +
        ' for ' +
        this.constructor.name +
        '.',
    );
    return listener(...args) as T;
  }

  /**
   * Fires an invocation and forget the result
   * @param namespace - The namespace
   * @param name - The name
   * @param args - The args
   */
  $fireInvocation(namespace: string, name: string, ...args: any[]): void {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    try {
      const namespacedListenerMap = this._$invokerListenersMap.get(namespace);
      assert(
        namespacedListenerMap !== undefined,
        'Invocation failed: ' +
          namespace +
          '/' +
          name +
          ' for ' +
          this.constructor.name +
          ' is not registered.',
      );
      const listener = namespacedListenerMap.get(name);
      assert(
        listener !== undefined,
        'Invocation failed: ' +
          namespace +
          '/' +
          name +
          ' for ' +
          this.constructor.name +
          ' is not registered.',
      );
      debug.log(
        '($fireInvocation): Running invocation ' +
          namespace +
          '/' +
          name +
          ' for ' +
          this.constructor.name +
          '.',
      );
      listener(...args);
    } catch (e) {
      let message = 'Unknown error.';
      if (e instanceof Error) {
        message = e.message;
      }
      debug.log(
        '($fireInvocation): Invocation for ' +
          namespace +
          '/' +
          name +
          ' failed with:  ' +
          message,
      );
    }
  }

  /**
   * Get a list of all registered invocations
   */
  $getInvocations() {
    const invocations = [];
    for (const namespace of this._$invokerListenersMap.keys()) {
      const namespacedListenerMap = this._$invokerListenersMap.get(namespace);
      if (namespacedListenerMap) {
        for (const name of namespacedListenerMap.keys()) {
          invocations.push({ namespace, name });
        }
      }
    }
    return invocations;
  }

  /**
   * Registers a yield listener
   * @param namespace - The namespace
   * @param name - The invoker name
   * @param listener - The listener function
   */
  $registerYield(
    namespace: string,
    name: string,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    assert(typeof listener === 'function', 'Invalid listener argument.');
    if (!this._$yielderListenersMap.has(namespace)) {
      this._$yielderListenersMap.set(namespace, new Map());
    }
    const namespacedListenerMap = this._$yielderListenersMap.get(
      namespace,
    ) as Map<string, GamoraFoundationGlueListenerType>;
    namespacedListenerMap.set(name, listener);
    return this;
  }

  /**
   * Unregisters an invoker listener
   * @param namespace - The namespace
   * @param name - The invoker name
   * @param listener - The listener function
   */
  $unregisterYield(namespace: string, name: string): this {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    if (this._$yielderListenersMap.has(namespace)) {
      const namespacedListenerMap = this._$yielderListenersMap.get(
        namespace,
      ) as Map<string, GamoraFoundationGlueListenerType>;
      namespacedListenerMap.delete(name);
      if (namespacedListenerMap.size === 0) {
        this._$yielderListenersMap.delete(namespace);
      }
    }
    return this;
  }

  /**
   * Executes the yield
   * @param namespace - The namespace
   * @param name - The event name
   * @param listener - The listener function
   */
  $executeYielder<T>(namespace: string, name: string, ...args: any[]): T {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    const namespacedListenerMap = this._$yielderListenersMap.get(namespace);
    assert(
      namespacedListenerMap !== undefined,
      'Yielder failed: ' +
        namespace +
        '/' +
        name +
        ' for ' +
        this.constructor.name +
        ' is not registered.',
    );
    const listener = namespacedListenerMap.get(name);
    assert(
      listener !== undefined,
      'Yielder failed: ' +
        namespace +
        '/' +
        name +
        ' for ' +
        this.constructor.name +
        ' is not registered.',
    );
    debug.log(
      '($yielder): Running yielder ' +
        namespace +
        '/' +
        name +
        ' for ' +
        this.constructor.name +
        '.',
    );
    return listener(...args) as T;
  }

  /**
   * Fires a yielder and forget the result
   * @param namespace - The namespace
   * @param name - The name
   * @param args - The args
   */
  $fireYielder(namespace: string, name: string, ...args: any[]): void {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    try {
      const namespacedListenerMap = this._$yielderListenersMap.get(namespace);
      assert(
        namespacedListenerMap !== undefined,
        'Yielder failed: ' +
          namespace +
          '/' +
          name +
          ' for ' +
          this.constructor.name +
          ' is not registered.',
      );
      const listener = namespacedListenerMap.get(name);
      assert(
        listener !== undefined,
        'Yielder failed: ' +
          namespace +
          '/' +
          name +
          ' for ' +
          this.constructor.name +
          ' is not registered.',
      );
      debug.log(
        '($fireYielder): Running yielder ' +
          namespace +
          '/' +
          name +
          ' for ' +
          this.constructor.name +
          '.',
      );
      listener(...args);
    } catch (e) {
      let message = 'Unknown error.';
      if (e instanceof Error) {
        message = e.message;
      }
      debug.log(
        '($fireYielder): Yielder for ' +
          namespace +
          '/' +
          name +
          ' failed with:  ' +
          message,
      );
    }
  }

  /**
   * Checks if a yielder exists
   * @param namespace - The namespace
   * @param name - The name
   */
  $hasYielder(namespace: string, name: string): boolean {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    const namespacedListenerMap = this._$yielderListenersMap.get(namespace);
    if (!namespacedListenerMap) {
      return false;
    }
    const listener = namespacedListenerMap.get(name);
    if (!listener) {
      return false;
    }
    return true;
  }

  /**
   * Get a list of all registered yielders
   */
  $getYielders() {
    const yields = [];
    for (const namespace of this._$yielderListenersMap.keys()) {
      const namespacedListenerMap = this._$yielderListenersMap.get(namespace);
      if (namespacedListenerMap) {
        for (const name of namespacedListenerMap.keys()) {
          yields.push({ namespace, name });
        }
      }
    }
    return yields;
  }

  /**
   * Registers a listener for an event
   * @param namespace - The namespace
   * @param name - The event name
   * @param listener - The listener function
   */
  $on(
    namespace: string,
    name: string,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    assert(typeof listener === 'function', 'Invalid listener argument.');
    if (!this._$eventListenersMap.has(namespace)) {
      this._$eventListenersMap.set(namespace, new Map());
    }
    const namespacedListenerMap = this._$eventListenersMap.get(
      namespace,
    ) as Map<string, GamoraFoundationGlueListenerType[]>;
    if (!namespacedListenerMap.has(name)) {
      namespacedListenerMap.set(name, []);
    }
    const listeners = namespacedListenerMap.get(
      name,
    ) as GamoraFoundationGlueListenerType[];
    listeners.push(listener);
    return this;
  }

  /**
   * Unregisters a listener for an event
   * @param namespace - The namespace
   * @param name - The event name
   * @param listener - The listener function
   */
  $off(
    namespace: string,
    name: string,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    assert(typeof listener === 'function', 'Invalid listener argument.');
    if (this._$eventListenersMap.has(namespace)) {
      const namespacedListenerMap = this._$eventListenersMap.get(
        namespace,
      ) as Map<string, GamoraFoundationGlueListenerType[]>;
      if (namespacedListenerMap.has(name)) {
        const listeners = namespacedListenerMap.get(
          name,
        ) as GamoraFoundationGlueListenerType[];
        const idx = listeners.indexOf(listener);
        if (idx !== -1) {
          listeners.splice(idx, 1);
        }
      }
    }
    return this;
  }

  /**
   * Checks if a listener exists
   * @param namespace - The namespace
   * @param name - The name
   */
  $hasListener(namespace: string, name: string): boolean {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    const namespacedListenerMap = this._$eventListenersMap.get(namespace);
    if (!namespacedListenerMap) {
      return false;
    }
    const listener = namespacedListenerMap.get(name);
    if (!listener) {
      return false;
    }
    return true;
  }

  /**
   * Get a list of all registered listeners
   */
  $getListeners() {
    const yields = [];
    for (const namespace of this._$yielderListenersMap.keys()) {
      const namespacedListenerMap = this._$yielderListenersMap.get(namespace);
      if (namespacedListenerMap) {
        for (const name of namespacedListenerMap.keys()) {
          yields.push({ namespace, name });
        }
      }
    }
    return yields;
  }

  /**
   * Emits new event
   * @param namespace - The namespace
   * @param name - The event name
   * @param args - Arguments to supply
   */
  $emit(namespace: string, name: string, ...args: any[]): void {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    assert(typeof name === 'string', 'Invalid name argument.');
    if (this._$eventListenersMap.has(namespace)) {
      const namespacedListenerMap = this._$eventListenersMap.get(
        namespace,
      ) as Map<string, GamoraFoundationGlueListenerType[]>;
      if (namespacedListenerMap.has(name)) {
        const listeners = namespacedListenerMap.get(
          name,
        ) as GamoraFoundationGlueListenerType[];
        for (const listener of listeners.slice()) {
          try {
            listener(...args);
          } catch (e) {
            let message = 'Unknown error.';
            if (e instanceof Error) {
              message = e.message;
            }
            debug.log(
              '($emit): Emit for ' +
                namespace +
                '/' +
                name +
                ' failed with:  ' +
                message,
            );
          }
        }
      }
    }
  }

  /**
   * Remove all listeners
   */
  $removeAllListeners(): void {
    this._$eventListenersMap = new Map();
    this._$yielderListenersMap = new Map();
    this._$invokerListenersMap = new Map();
  }

  /**
   * Remove invoker listeners
   */
  $removeInvokerListenersByNamespace(namespace: string): void {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    if (this._$invokerListenersMap.has(namespace)) {
      this._$invokerListenersMap.delete(namespace);
    }
  }

  /**
   * Remove yielder listeners
   */
  $removeYielderListenersByNamespace(namespace: string): void {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    if (this._$yielderListenersMap.has(namespace)) {
      this._$yielderListenersMap.delete(namespace);
    }
  }

  /**
   * Remove event listeners
   */
  $removeEventListenersByNamespace(namespace: string): void {
    assert(typeof namespace === 'string', 'Invalid namespace argument.');
    if (this._$eventListenersMap.has(namespace)) {
      this._$eventListenersMap.delete(namespace);
    }
  }

  /**
   * Remove event listeners
   */
  $removeEventListenersByNamespaceAndName(
    namespace: string,
    name: string,
  ): void {
    assert(typeof name === 'string', 'Invalid namespace argument.');
    assert(typeof namespace === 'string', 'Invalid name argument.');
    if (this._$eventListenersMap.has(namespace)) {
      const namespacedListenerMap = this._$eventListenersMap.get(
        namespace,
      ) as Map<string, GamoraFoundationGlueListenerType[]>;
      namespacedListenerMap.delete(name);
      if (namespacedListenerMap.size === 0) {
        this._$eventListenersMap.delete(namespace);
      }
    }
  }
}
